// AthleteProfileMixin.vue

import { Component, Mixins, Prop } from 'vue-property-decorator';
import { AppHostnameMixin } from '@/mixins'
import { AthleteProfileModel, BAEventModel, EventLocationModel } from '@/models';
import { athleteApi } from '@/api/AthleteApi';
import { baPurchaseApi } from '@/api/BAPurchaseApi';
import { baEventApi } from '@/api/BAEventsApi';
import { userStore } from '@/store';
import { isEmpty, isNotEmpty } from '@/pipes';
import { BAEventStatus } from '@/models/baEvent/BAEventModel';
import { ObjectCache } from '@/helpers/object-cache';
import { RepositoryQuery } from '@best-athletes/ba-types';

/**
 * Provides helpers to access athlete profile
 */
@Component
export class AthleteProfileMixin extends Mixins(AppHostnameMixin) {
	@Prop({ type: String, required: true }) athleteId;

	mounted() {
		try {
			this.loadAthleteProfile();
		} catch(e) {
			console.log(`Error loading athlete profile: `, e)
		}
	}

    profileError: string;
	athleteProfile: AthleteProfileModel = new AthleteProfileModel;
	isLoadingAthleteProfile = false;
	isAthleteLoaded: boolean = false;
	locationsCache: ObjectCache<EventLocationModel> = new ObjectCache<EventLocationModel>();
	async loadAthleteProfile() {
		try{
			let athleteId = this.AthleteId;
			if( !athleteId ) athleteId = this.athleteId;
			if( !athleteId ) athleteId = userStore.athleteId;
			if( !athleteId ) athleteId = userStore.athlete?.id;
			if( !athleteId ) {
				console.warn(`No athlete id provided`);
				return;
			}

			this.isLoadingAthleteProfile = true;
			this.isAthleteLoaded = false;

			this.athleteProfile = await athleteApi.getFullProfile(athleteId);
			if( isEmpty(this.athleteProfile) ) {
				throw( Error(`No athlete profile found for ${athleteId}`));
			}

			this.athleteProfile.purchases = await baPurchaseApi.findByAthleteId(athleteId);

			// load athlete events
			if( isEmpty(this.athleteProfile.events) ) this.athleteProfile.events = [];
			if( isNotEmpty(this.athleteProfile.purchases) ) {
				for( const purchase of this.athleteProfile.purchases ) {
					if( this.athleteProfile.events.find(e => e.id === purchase.productId) ) continue;
					const event: BAEventModel = await baEventApi.findById(purchase.productId);
					if( isNotEmpty(event) && event.status != BAEventStatus.deleted ) this.athleteProfile.events.push(event);
				}
			}

			// find associated events
			if( isNotEmpty(this.athleteProfile.events) ) {
				// Obtain associated events
				this.athleteProfile.associatedEvents = [];
				for( const event of this.athleteProfile.events ) {
					const query: RepositoryQuery<BAEventModel> = {$match: {prerequisite: event.id}};
					const response = await baEventApi.queryAll(query);
					this.athleteProfile.associatedEvents.push(...response.docs.filter(e => !e.invitational));
				};
			}

			// load events into schedule
			if( isNotEmpty(this.athleteProfile.events) ) {
				if( !this.athleteProfile.schedule ) this.athleteProfile.schedule = [];
				for( const ev of this.athleteProfile.Events ) {
					if( !this.IsEventPurchased(ev) ) continue;
					await ev.getLocation(this.locationsCache);
					const event = this.athleteProfile.schedule.find(s => s.name === ev.name );
					if( !event ) {
						this.athleteProfile.schedule.push({
							name: ev.name, 
							eventId: ev.id,
							description: ev.description,
							date: new Date(ev.date),
							location: ev.LocationName,
							url: ev.Online? ev.meetingUrl : ev.url,
							purchased: true,
							online: ev.Online,
						})
					} else {
						event.description = ev.description;
						event.date = new Date(ev.date);
						event.location = ev.LocationName;
						event.url = ev.url;
						const today = new Date();
						if( event.date >= today ) event.archive = false;
						event.purchased = true;
					}
				}
			}

			// remove duplicates
			const seenNames = new Set<string>();
			const uniqueSchedule = this.athleteProfile.schedule.filter(item => {
				if (seenNames.has(item.name)) {
					return false;
				} else {
					seenNames.add(item.name);
					return true;
				}
			});
			this.athleteProfile.schedule = uniqueSchedule;
		} catch(e) {
            this.profileError = e.message;
			this.isLoadingAthleteProfile = false;
			console.error(`Failed to load athlete profile (${this.athleteId}): `, e);
		}
		this.isAthleteLoaded = true;
		this.isLoadingAthleteProfile = false;
	}
	get IsLoadingAthleteProfile(): boolean {
		return this.isLoadingAthleteProfile || !this.athleteProfile || !this.athleteProfile.id;
	}
    get ProfileError(): string {
        if( !this.profileError ) return 'ok';
        return this.profileError;
    }
    get AthleteId(): string {
		if( this.AthleteAppActive && !!userStore.athleteId ) return userStore.athleteId;
		return this.athleteId;
	}
	get AthleteName(): string {
		if( !!this.athleteProfile ) return this.athleteProfile.FullName;
		if( !!userStore ) return userStore.FullName;
		return '-';
	}

    IsEventPurchased(event: BAEventModel): boolean {
        if( isEmpty(this.athleteProfile) ) return false;
        const purchaseOfEvent = this.athleteProfile.purchases.find(p => p.productId === event.id);
        return purchaseOfEvent?.completed;
    }
}
